import React, { useCallback, useEffect, useState } from "react";
import {
  Collapse,
  Button,
  Divider,
  message,
  Popconfirm,
  Pagination,
  Row,
  Col,
  Input,
  Select,
  Upload,
  Modal,
  App,
} from "antd";

import {
  List_QUIZ_QUESTION,
  DELETE_QUIZ_QUESTION,
  BULK_QUIZ_QUESTION,
  BULD_IMPORT_QUIZ_QUESTION_TEMPLATE
} from "../../../../../apis/apis";

import CulsightPageLoader from "../../../../../components/CulsightPageLoader";
import { UploadOutlined } from "@ant-design/icons";

import AddQuizQuestion from "./AddQuizQuestion";
import EditQuizQuestion from "./EditQuizQuestion";
import debounce from "lodash.debounce";

const { Panel } = Collapse;

const QuizQuestion = (props) => {
  const { notification } = App.useApp();
  const { Option } = Select;

  const [items, setItems] = useState([]);
  const [page_loader, set_page_loader] = useState(true);
  const [activePanelKey, setActivePanelKey] = useState(null);

  const [questionToDelete, setQuestionToDelete] = useState(null);
  const [addQuestionView, setAddQuestionView] = useState(false);

  const [current_page, set_current_page] = useState(1);
  const [total_pages, set_total_pages] = useState(0);
  const [total_questions, set_total_questions] = useState(0);

  const [errors, set_errors] = useState("");
  const [file, set_file] = useState([]);
  const [is_model_open, set_is_model_open] = useState(false);

  
  const showModal = () => set_is_model_open(true);
  const handleCancel = () => set_is_model_open(false);

  
  const beforeUpload = (file) => {
    const isCSV = file.type === "text/csv";
    if (!isCSV) {
      set_errors({ file: "Only CSV files are allowed!" });
      message.error("Please upload a valid CSV file.");
      return Upload.LIST_IGNORE;
    }

    set_file([file]); 
    set_errors("");
    message.success(file.name);
    return false;
  };



  const handleDownloadTemplate = async () => {
      try {
        const response = await BULD_IMPORT_QUIZ_QUESTION_TEMPLATE();
    
        const blob = new Blob([response.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
    
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
    
        link.href = url;
        link.download = "bulk_add_quiz_question_template.csv"; 
        document.body.appendChild(link);
        link.click();
        link.remove();
    
        notification.success({
          message: "Template Downloaded",
          description: "Excel template downloaded successfully",
        });
      } catch (error) {
        notification.error({
          message: "Download Failed",
          description: "Something went wrong",
        });
      }
    };
    

  
  const handleBulkUpload = async () => {
    if (file.length === 0) {
      set_errors({ file: "Please select a CSV file before submitting." });
      return;
    }

    set_page_loader(true);
    const formData = new FormData();
    formData.append("chapter_id", atob(props.chapter_id));
    formData.append("file", file[0]);

    try {
      const response = await BULK_QUIZ_QUESTION(formData);

      if (response?.data?.status) {
        notification.success({
          message: "Successful",
          description: response?.data?.message,
        });

        
        set_is_model_open(false);
        set_file([]);
        set_errors("");

        
        await refetchData();
      } else {
        set_errors(response?.data?.errors);
        message.error(response?.data?.message || "Bulk upload failed!");
      }
    } catch (error) {
      message.error(
        "Server Error: " + (error?.response?.data?.message || "Unknown error")
      );
    } finally {
      set_page_loader(false);
    }
  };

 
  const refetchData = async () => {
    const FORM_DATA = new FormData();
    FORM_DATA.append("chapter_id", atob(props.chapter_id));

    const LIST_API_RESPONSE = await List_QUIZ_QUESTION(FORM_DATA);

    if (LIST_API_RESPONSE?.data?.status) {
      setItems(LIST_API_RESPONSE.data.data);
      set_current_page(LIST_API_RESPONSE.data.current_page);
      set_total_pages(LIST_API_RESPONSE.data.total_pages);
      set_total_questions(LIST_API_RESPONSE.data.total_questions);
      set_page_loader(false);
    }
  };

  useEffect(() => {
    refetchData();
  }, [props.chapter_id]);

  const pagination_on_change = async (page) => {
    set_page_loader(true);

    const FORM_DATA = new FormData();
    FORM_DATA.append("page", page);
    FORM_DATA.append("chapter_id", atob(props.chapter_id));

    const LIST_API_RESPONSE = await List_QUIZ_QUESTION(FORM_DATA);

    if (LIST_API_RESPONSE?.data?.status) {
      setItems(LIST_API_RESPONSE.data.data);
      set_current_page(LIST_API_RESPONSE.data.current_page);
      set_total_questions(LIST_API_RESPONSE.data.total_questions);
      set_page_loader(false);
    }
  };

 
  const confirmDelete = (id) => setQuestionToDelete(id);

  const handleDeleteConfirmed = async () => {
    try {
      const FORM_DATA = new FormData();
      FORM_DATA.append("id", questionToDelete);

      const DELETE_RESPONSE = await DELETE_QUIZ_QUESTION(FORM_DATA);

      if (DELETE_RESPONSE?.data?.status) {
        message.success("Deleted successfully");
        setItems((prev) => prev.filter((item) => item.id !== questionToDelete));
      } else {
        message.error("Failed to delete question");
      }
    } finally {
      setQuestionToDelete(null);
    }
  };

  // Search
  const fetchResults = useCallback(
    (search_key, search_value) => {
      debounce(async () => {
        const FORM_DATA = new FormData();
        FORM_DATA.append("chapter_id", atob(props.chapter_id));
        FORM_DATA.append(search_key, search_value);

        const LIST_API_RESPONSE = await List_QUIZ_QUESTION(FORM_DATA);

        if (LIST_API_RESPONSE?.data?.status) {
          setItems(LIST_API_RESPONSE.data.data);
          set_current_page(LIST_API_RESPONSE.data.current_page);
        }
      }, 500)();
    },
    [props.chapter_id]
  );

  const handleInput = (e) => fetchResults("topic", e.target.value);

  const selectBefore = (
    <Select defaultValue="topic">
      <Option value="topic">Topic</Option>
    </Select>
  );

  // Add question
  const handleQuestionAdded = async () => {
    setAddQuestionView(false);
    await refetchData();
  };

  return (
    <div>
      {page_loader ? (
        <CulsightPageLoader />
      ) : (
        <>
          <div style={{ minHeight: "60px", display: "block", position: "relative" }}>
            <Divider orientation="left">
              {addQuestionView ? "Add Question" : "Question List"}
            </Divider>

            

            <div style={{ position: "absolute", right: "0px", top: "0px" }}>
               <Button
                type="primary"
                size="small"
                onClick={handleDownloadTemplate}
                style={{ marginRight: "5px" }}
              >
               Download Template
              </Button>

              <Button
                type="primary"
                size="small"
                onClick={showModal}
                style={{ marginRight: "5px" }}
              >
                Bulk Add Questions
              </Button>

              <Button
                type="primary"
                size="small"
                onClick={() => setAddQuestionView(!addQuestionView)}
              >
                {addQuestionView ? "List Questions" : "Add New Question"}
              </Button>
            </div>
          </div>

          {addQuestionView ? (
            <AddQuizQuestion
              chapter_id={props.chapter_id}
              course_id={props.course_id}
              onSuccess={handleQuestionAdded}
            />
          ) : (
            <>
              <Row style={{ marginBottom: "15px" }}>
                <Col span={12}>
                  <Input
                    addonBefore={selectBefore}
                    onChange={handleInput}
                    placeholder="Search by Topic"
                    size="large"
                  />
                </Col>
              </Row>

              <Collapse>
                {items.map((item, index) => (
                  <Panel
                    header={`${current_page}.${index + 1} ${item.topic}`}
                    key={item.id}
                    extra={
                      <Popconfirm
                        title="Delete question?"
                        onConfirm={handleDeleteConfirmed}
                        onCancel={() => setQuestionToDelete(null)}
                        okText="Yes"
                        cancelText="No"
                        okButtonProps={{ danger: true }}
                      >
                        <Button
                          size="small"
                          danger
                          onClick={(e) => {
                            e.stopPropagation();
                            confirmDelete(item.id);
                          }}
                        >
                          Delete
                        </Button>
                      </Popconfirm>
                    }
                  >
                    <EditQuizQuestion
                      question_details={item}
                      chapter_id={props.chapter_id}
                      course_id={props.course_id}
                      quiz_question_id={btoa(item.id.toString())}
                    />
                  </Panel>
                ))}
              </Collapse>

              <Pagination
                style={{ marginTop: "15px", float: "right" }}
                onChange={pagination_on_change}
                current={current_page}
                total={total_questions}
                pageSize={5}
              />
            </>
          )}
        </>
      )}

      {/* Bulk Upload Modal */}
      <Modal
        title="Add Questions"
        open={is_model_open}
        onCancel={handleCancel}
        footer={[
          <Button
            type="primary"
            onClick={handleBulkUpload}
            style={{
        width: "100%",
        backgroundColor: "green",
        borderColor: "green",
      }}
          >
            Add
          </Button>,
        ]}
        width={400}
      >
        <Upload
          beforeUpload={beforeUpload}
          fileList={file}
          onRemove={() => set_file([])}
          accept=".csv"
        >
          <Button type="primary" icon={<UploadOutlined />} block style={{
      width:"350px",}}>
            Upload File
          </Button>
         
        </Upload>

        {errors?.file && <span style={{ color: "red" }}>{errors.file}</span>}
      </Modal>
    </div>
  );
};

export default QuizQuestion;
